import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MockDataProvider } from './features/mock-data';
import { TooltipProvider } from '@tonic/shared/components/ui';
import { CoreLayout } from './layout/CoreLayout';
import { DashboardPage } from './features/dashboard/pages/DashboardPage';
import { dashboardRoutes } from './features/dashboard/routes';
import { customerRoutes } from './features/customers/routes';
import { productsRoutes } from './features/products/routes';
import { inventoryRoutes } from './features/inventory';
import { reCommerceRoutes } from './features/re-commerce/routes';
import { aiRoutes } from './features/ai/routes';
import { settingsRoutes } from './features/settings/routes';
import { reportsRoutes } from './features/reports/routes';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});
// Determine if we should use mock data
const useMockData = import.meta.env.MODE === 'development' || import.meta.env.VITE_USE_MOCK_DATA === 'true';


// Create router with all routes
const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/admin" replace />
  },
  {
    path: '/admin',
    element: <CoreLayout />,
    children: [
      {
        index: true,
        element: <DashboardPage />
      },
      {
        path: 'dashboard/*',
        element: dashboardRoutes.element,
        children: dashboardRoutes.children
      },
      {
        path: 'customers/*',
        element: customerRoutes.element,
        children: customerRoutes.children
      },
      {
        path: 'products/*',
        element: productsRoutes.element,
        children: productsRoutes.children
      },
      {
        path: 'inventory/*',
        element: inventoryRoutes.element,
        children: inventoryRoutes.children
      },
      {
        path: 're-commerce/*',
        element: reCommerceRoutes.element,
        children: reCommerceRoutes.children
      },
      {
        path: 'ai/*',
        element: aiRoutes.element,
        children: aiRoutes.children
      },
      {
        path: 'settings/*',
        element: settingsRoutes.element,
        children: settingsRoutes.children
      },
      {
        path: 'reports/*',
        element: reportsRoutes.element,
        children: reportsRoutes.children
      }
    ]
  },
  {
    path: 'auth/*',
    element: <Navigate to="/admin" replace />
  },
  {
    path: '*',
    element: <Navigate to="/admin" replace />
  }
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {useMockData ? (
          <MockDataProvider>
            <RouterProvider router={router} />
          </MockDataProvider>
        ) : (
          <RouterProvider router={router} />
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
}
